'use client'

import { usePathname } from 'next/navigation'
import { AlertCircle, ArrowRight, Eye, Hash, Phone, Receipt, ShieldCheck, Zap } from 'lucide-react'
import { resolveColorHex, getVendorPrimaryColor } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import { useOrderLookup } from '@/lib/commerce/hooks'

const STEPS = [
  { num: '01', title: 'Захиалгын дугаар', body: 'Имэйл болон SMS-ээр ирсэн баталгаажуулалтын захиалгын дугаарыг олно уу. Жишээ : DS25111222.', icon: Receipt },
  { num: '02', title: 'Бүртгэлтэй утас', body: 'Захиалгаа үүсгэхдээ оруулсан 8 оронтой утасны дугаараа бичнэ.', icon: Phone },
  { num: '03', title: 'Захиалгаа харах', body: 'Хүргэлтийн явц, төлбөрийн мэдээлэл зэрэг бүх мэдээллээ авах боломжтой.', icon: Eye },
] as const

function Eyebrow({ children, color }: { children: any, color?: string }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: color || 'var(--muted-foreground)' }}>
      <span className="h-px w-6 bg-current opacity-40" />
      {children}
    </div>
  )
}

export default function OrderCheck({ vendor, notFound, initialOrderNum, initialPhoneNum }: { vendor?: any, notFound?: boolean, initialOrderNum?: string, initialPhoneNum?: string }) {
  const pathname = usePathname()
  const primary = resolveColorHex(getVendorPrimaryColor(vendor))
  const { orderNum, setOrderNum, phoneNum, setPhoneNum, errors, isLoading, submit } = useOrderLookup({ initialOrderNum, initialPhoneNum })

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div className="bg-white">
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 py-10 lg:py-8">
        {/* Hero panel */}
        <div className="relative rounded-3xl overflow-hidden border border-border bg-white">
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(120deg, color-mix(in oklch, ${primary} 14%, white) 0%, color-mix(in oklch, ${primary} 6%, white) 45%, white 100%)` }}
          />
          <svg className="absolute inset-0 w-full h-full opacity-50" aria-hidden="true">
            <defs>
              <pattern id="ocgrid" width="32" height="32" patternUnits="userSpaceOnUse">
                <path d="M32 0H0V32" fill="none" stroke={primary} strokeOpacity="0.06" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#ocgrid)" />
          </svg>

          <div className="relative grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-7 px-6 lg:px-12 pt-12 lg:pt-16 pb-10 lg:pb-8">
              <Eyebrow color={primary}>Захиалга</Eyebrow>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[68px] font-bold tracking-[-0.025em] leading-[0.98] text-foreground">
                Захиалгаа<br />
                <span style={{ background: `linear-gradient(to right, ${primary}, color-mix(in oklch, ${primary} 50%, white))`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                  шалгая.
                </span>
              </h1>
              <p className="mt-5 text-[15.5px] text-muted-foreground leading-relaxed max-w-lg">
                Нэвтрэх шаардлагагүйгээр захиалгын дугаар болон утасны дугаараараа хүргэлтийн явц, төлбөрийн мэдээлэлээ хараарай.
              </p>

              {notFound && (
                <div className="mt-6 max-w-2xl flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
                  <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-[14px] font-semibold text-red-700">Захиалга олдсонгүй</div>
                    <div className="mt-0.5 text-[13px] text-red-600/90 leading-relaxed">
                      Захиалгын дугаар болон утасны дугаараа шалгаад дахин оролдоно уу.
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 rounded-2xl bg-white border border-border shadow-[0_20px_50px_-25px_rgba(15,23,42,0.15)] p-5 lg:p-6 max-w-2xl">
                <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr_auto] gap-3.5 lg:items-end">
                  <Input
                    label="Захиалгын дугаар"
                    value={orderNum}
                    onChange={(e) => setOrderNum(e.target.value)}
                    placeholder="DS25111222"
                    leftIcon={Hash}
                    color={vendor?.color?.primary}
                    onKeyDown={onKey}
                  />
                  <Input
                    label="Утасны дугаар"
                    type="tel"
                    inputMode="numeric"
                    value={phoneNum}
                    onChange={(e) => setPhoneNum(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    placeholder="99999999"
                    leftIcon={Phone}
                    color={vendor?.color?.primary}
                    onKeyDown={onKey}
                    autoComplete="tel"
                  />
                  <Button
                    onClick={submit}
                    isLoading={isLoading}
                    loadingMessage="Уншиж байна"
                    color={vendor?.color?.primary}
                    icon={ArrowRight}
                    iconPosition="right"
                    className="lg:h-10!"
                  >
                    Шалгах
                  </Button>
                </div>
                {(errors.orderNum || errors.phoneNum) && (
                  <div className="mt-3 text-[12px] text-red-600 font-medium">
                    {errors.orderNum || errors.phoneNum}
                  </div>
                )}
              </div>

              <div className="mt-5 flex items-center gap-4 text-[12px] text-muted-foreground flex-wrap">
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />Хялбар</span>
                <span className="h-3 w-px bg-border" />
                <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" style={{ color: primary }} />Шуурхай</span>
                <span className="h-3 w-px bg-border" />
                <a href={navigateUrl('/login', pathname)} className="font-semibold hover:underline" style={{ color: primary }}>
                  Бүртгэлтэй бол нэвтрэх →
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative px-6 pb-10 lg:p-0 lg:pr-6">
              <div className="lg:absolute lg:inset-0 lg:p-6 grid place-items-center">
                <BoxArt color={primary} />
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-14 lg:mt-20">
          <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
            <div>
              <Eyebrow>Хэрхэн ашиглах</Eyebrow>
              <h2 className="mt-2 text-3xl lg:text-[40px] font-bold tracking-[-0.015em] text-foreground">3 алхмаар захиалгаа харна.</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {STEPS.map((s) => {
              const Icon = s.icon
              return (
                <div key={s.num} className="relative rounded-2xl bg-white border border-border p-6 hover:-translate-y-0.5 transition-transform">
                  <div className="flex items-start justify-between">
                    <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `color-mix(in oklch, ${primary} 12%, white)`, color: primary }}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="text-[40px] font-bold tabular-nums leading-none tracking-[-0.02em]" style={{ color: `color-mix(in oklch, ${primary} 14%, white)` }}>{s.num}</div>
                  </div>
                  <div className="mt-5 text-[18px] font-semibold tracking-[-0.005em] text-foreground">{s.title}</div>
                  <p className="mt-1.5 text-[13.5px] text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function BoxArt({ color }: { color: string }) {
  return (
    <svg viewBox="0 0 480 360" className="w-full h-full max-w-md" aria-hidden="true">
      <defs>
        <linearGradient id="ocBoxTop" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.95" />
          <stop offset="100%" stopColor={color} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="ocBoxFront" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.7" />
        </linearGradient>
      </defs>
      <circle cx="240" cy="190" r="150" fill={color} fillOpacity="0.06" />
      <circle cx="240" cy="190" r="100" fill={color} fillOpacity="0.08" />

      <path d="M60 280 Q 160 200 240 220 T 420 120" stroke={color} strokeOpacity="0.35" strokeWidth="2.5" strokeDasharray="2 8" strokeLinecap="round" fill="none" />
      <circle cx="60" cy="280" r="6" fill={color} />
      <circle cx="60" cy="280" r="14" fill={color} fillOpacity="0.2" />
      <circle cx="420" cy="120" r="8" fill="#ffffff" stroke={color} strokeWidth="3" />

      <g transform="translate(150 110)">
        <ellipse cx="90" cy="190" rx="100" ry="10" fill="#000" fillOpacity="0.08" />
        <polygon points="0,40 90,0 180,40 90,80" fill="url(#ocBoxTop)" />
        <polygon points="0,40 90,80 90,180 0,140" fill="#1e293b" fillOpacity="0.12" />
        <polygon points="180,40 90,80 90,180 180,140" fill="url(#ocBoxFront)" stroke="rgba(15,23,42,0.08)" />
        <rect x="86" y="0" width="8" height="180" fill={color} fillOpacity="0.7" />
        <polygon points="86,0 94,0 90,80" fill={color} fillOpacity="0.85" />
        <rect x="105" y="95" width="55" height="32" fill="#fff" stroke="rgba(15,23,42,0.18)" rx="2" />
        <rect x="110" y="100" width="32" height="3" fill={color} fillOpacity="0.7" />
        <rect x="110" y="106" width="44" height="2" fill="rgba(15,23,42,0.4)" />
        <rect x="110" y="111" width="38" height="2" fill="rgba(15,23,42,0.3)" />
        <rect x="110" y="116" width="22" height="6" fill="#000" />
      </g>

      <g fill={color} fillOpacity="0.6">
        <circle cx="100" cy="120" r="3" />
        <circle cx="380" cy="240" r="4" />
        <circle cx="80" cy="180" r="2" />
      </g>
    </svg>
  )
}
